import { CloseOutlined, StarFilled, StarOutlined } from '@ant-design/icons';
import { Result, Skeleton } from 'antd';
import AskMidwifeCard from 'components/info/AskMidwifeCard';
import CategoryIcon from 'components/info/CategoryIcon';
import CategoryPageItems from 'components/info/CategoryPageItems';
import { useBookmarkCategory, useCategoryPage } from 'components/info/queries';
import PageTopBar from 'components/navigation/PageTopBar';
import { colors, radius } from 'design';
import useScrollRestoration from 'hooks/useScrollRestoration';
import posthog from 'posthog-js';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createUseStyles } from 'react-jss';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { CategoryItemDTO } from 'services/api/models/CategoryDTO';
import onArticleSelected from 'utils/ContentUtils';

const useStyles = createUseStyles({
  pageContainer: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    backgroundColor: colors.surface.page,
    alignItems: 'center',
  },
  container: {
    height: '100%',
    overflowY: 'scroll',
    width: '100%',
  },
  // The cover image, like an article's: edge to edge above the content
  cover: {
    width: '100%',
    height: 200,
    objectFit: 'cover',
    display: 'block',
  },
  content: {
    maxWidth: 600,
    margin: '0 auto',
    padding: '8px 8px 32px',
    display: 'flex',
    flexDirection: 'column',
    gap: 28,
  },
  header: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    paddingTop: 12,
    '& h2': {
      margin: 0,
      fontWeight: 300,
    },
    '& p': {
      margin: 0,
      color: colors.text.secondary,
    },
  },
  headerIcon: {
    width: 40,
    height: 40,
    borderRadius: radius.sm,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.surface.calm,
    fontSize: 20,
  },
  counts: {
    fontSize: '0.9em',
    color: colors.text.secondary,
  },
  progress: {
    height: 5,
    borderRadius: 3,
    backgroundColor: colors.surface.calm,
    overflow: 'hidden',
    '& i': {
      display: 'block',
      height: '100%',
      backgroundColor: colors.brand.primaryStrong,
    },
  },
  titleRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
  },
  bookmark: {
    flexShrink: 0,
    width: 40,
    height: 40,
    borderRadius: radius.circle,
    border: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.surface.calm,
    color: colors.text.secondary,
    fontSize: 20,
    cursor: 'pointer',
  },
  bookmarked: {
    composes: '$bookmark',
    backgroundColor: colors.brand.accentSubtle,
    color: colors.brand.accentStrong,
  },
  startHere: {
    backgroundColor: colors.surface.inverse,
    color: colors.text.inverse,
    borderRadius: radius.md,
    padding: 14,
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    cursor: 'pointer',
    '& small': {
      textTransform: 'uppercase',
      letterSpacing: '0.05em',
      fontWeight: 600,
      color: colors.brand.primary,
    },
    '& b': {
      fontWeight: 'normal',
      fontSize: '1.1em',
    },
  },
  chips: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 10,
  },
  chip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '8px 14px',
    borderRadius: radius.pill,
    border: `1px solid ${colors.border.default}`,
    backgroundColor: colors.surface.base,
    fontWeight: 500,
    fontSize: '0.95em',
    cursor: 'pointer',
    '& small': {
      color: colors.text.secondary,
    },
  },
  // The selected chip reads as an active filter, with a cross to remove it
  chipSelected: {
    composes: '$chip',
    borderColor: colors.surface.inverse,
    backgroundColor: colors.surface.inverse,
    color: colors.text.inverse,
    '& small': {
      color: colors.text.inverse,
    },
    '& .anticon': {
      fontSize: 11,
    },
  },
});

export default function InfoCategoryPage() {
  const styles = useStyles();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { slug } = useParams();
  const location = useLocation();
  const scrollableRef = useRef<HTMLDivElement>(null);
  useScrollRestoration(scrollableRef);

  const { data: page, isPending, isError } = useCategoryPage(slug as string);
  const bookmark = useBookmarkCategory(slug as string);

  // The chips name the themes and sections of the page: selecting one shows
  // only that one, selecting it again (or its cross) shows everything. The
  // selection lives in the history entry, next to the scroll position, so
  // coming back from an article or from another category finds the page as
  // it was left (the route mounts one instance per category, see App).
  const [selectedChip, setSelectedChip] = useState<number | null>(location.state?.chip ?? null);
  const toggleChip = (id: number) => {
    const next = selectedChip === id ? null : id;
    posthog.capture('info.category_filtered', { category_slug: slug, selected: next !== null });
    setSelectedChip(next);
    navigate(location.pathname + location.search, { replace: true, state: { ...location.state, chip: next } });
  };

  const toggleBookmark = () => {
    if (!page) return;
    const next = !page.is_bookmarked;
    posthog.capture('info.category_bookmarked', { category_slug: slug, bookmarked: next });
    bookmark.mutate(next);
  };

  const back = () => {
    posthog.capture('info.category_closed', { category_slug: slug });
    navigate(-1);
  };

  const chips = (page?.items ?? []).flatMap((item: CategoryItemDTO) =>
    item.type === 'category' && item.presentation !== 'link' ? [item.category] : [],
  );
  const parentName = page && page.path.length > 1 ? page.path[page.path.length - 2].name : t('menu.info');
  const startHere = page?.start_here ?? undefined;
  const openStartHere = () => {
    if (page && startHere) {
      onArticleSelected(startHere, 0, navigate, dispatch, {
        opened_on_page: 'info-start-here',
        found_using_category: page.slug,
      });
    }
  };

  return (
    <div className={styles.pageContainer}>
      <PageTopBar title={parentName} backButton onBackButtonClicked={back} />
      <div className={styles.container} ref={scrollableRef} data-testid="category-page">
        {page?.cover_image_url && (
          <img src={page.cover_image_url} alt="" className={styles.cover} data-testid="category-cover" />
        )}
        <div className={styles.content}>
          {isPending ? (
            <>
              <Skeleton active paragraph={{ rows: 2 }} />
              <Skeleton.Input block active style={{ height: 64 }} />
              <Skeleton.Input block active style={{ height: 64 }} />
            </>
          ) : isError ? (
            <Result status="error" subTitle={t('loginRegister.errors.errorOccurred')} />
          ) : (
            page && (
              <>
                <div className={styles.header}>
                  {(page.icon || page.icon_url) && (
                    <span className={styles.headerIcon}>
                      <CategoryIcon icon={page.icon} iconUrl={page.icon_url} />
                    </span>
                  )}
                  <div className={styles.titleRow}>
                    <h2>{page.name}</h2>
                    <button
                      type="button"
                      className={page.is_bookmarked ? styles.bookmarked : styles.bookmark}
                      data-testid="bookmark-button"
                      aria-pressed={page.is_bookmarked}
                      aria-label={t(
                        page.is_bookmarked ? 'infoPage.structure.unbookmark' : 'infoPage.structure.bookmark',
                      )}
                      title={t(page.is_bookmarked ? 'infoPage.structure.unbookmark' : 'infoPage.structure.bookmark')}
                      onClick={toggleBookmark}
                    >
                      {page.is_bookmarked ? <StarFilled /> : <StarOutlined />}
                    </button>
                  </div>
                  {page.description && <p>{page.description}</p>}
                  {page.show_count && (
                    <span className={styles.counts}>
                      {t('infoPage.structure.articles', { count: page.post_count })}
                      {page.read_count > 0 && ` · ${t('infoPage.structure.read', { count: page.read_count })}`}
                    </span>
                  )}
                  {page.show_progress && page.show_count && page.read_count > 0 && (
                    <div
                      className={styles.progress}
                      role="progressbar"
                      aria-valuemin={0}
                      aria-valuemax={page.post_count}
                      aria-valuenow={page.read_count}
                    >
                      <i style={{ width: `${Math.round((100 * page.read_count) / page.post_count)}%` }} />
                    </div>
                  )}
                </div>
                {startHere && !startHere.read && (
                  <div
                    className={styles.startHere}
                    data-testid="start-here"
                    onClick={openStartHere}
                    onKeyDown={e => {
                      if (e.key === 'Enter') openStartHere();
                    }}
                    role="button"
                    tabIndex={0}
                  >
                    <small>{t('infoPage.structure.startHere')}</small>
                    <b>{startHere.title}</b>
                  </div>
                )}
                {chips.length > 1 && (
                  <div className={styles.chips}>
                    {chips.map(chip => (
                      <span
                        key={chip.id}
                        className={selectedChip === chip.id ? styles.chipSelected : styles.chip}
                        data-testid={`chip-${chip.slug}`}
                        role="button"
                        aria-pressed={selectedChip === chip.id}
                        tabIndex={0}
                        onClick={() => toggleChip(chip.id)}
                        onKeyDown={e => {
                          if (e.key === 'Enter') toggleChip(chip.id);
                        }}
                      >
                        {chip.name}
                        {(chip.show_count || chip.kind === 'section') && <small>{chip.post_count}</small>}
                        {selectedChip === chip.id && <CloseOutlined />}
                      </span>
                    ))}
                  </div>
                )}
                <CategoryPageItems page={page} selectedId={selectedChip} />
                <AskMidwifeCard source={`info-category:${page.slug}`} />
              </>
            )
          )}
        </div>
      </div>
    </div>
  );
}
